// SPDX-License-Identifier: GPL-3.0-or-later
// Which screen the game page shows. A pure function of the game phase and the pause flag.

import { FEATURED_TURN_MAX_MS, type GamePhase } from "../src/shared/contracts.ts";

/** The screens of the game page. "none" means the table is in play. */
export type Screen = "none" | "start" | "pause" | "over";

/** True while a game runs (a ball is ready, in play, or the next ball is on its way). */
export function isGameActive(phase: GamePhase): boolean {
  return phase === "ball_ready" || phase === "playing" || phase === "ball_end";
}

/** True when no game runs. A featured turn can start only then. */
export function isGameIdle(phase: GamePhase): boolean {
  return !isGameActive(phase);
}

/** Choose the screen. Pause wins over play. The start and game over screens need no pause. */
export function screenFor(phase: GamePhase, paused: boolean): Screen {
  if (phase === "attract") return "start";
  if (phase === "game_over") return "over";
  return paused ? "pause" : "none";
}

/** The reason text for the end of a featured turn. It never blames the player. */
export function endReasonText(
  reason: "game_over" | "time_limit" | "operator" | "disconnect",
): string {
  switch (reason) {
    case "game_over":
      return "Your featured turn is finished. Thank you for playing.";
    case "time_limit":
      return `The time limit of ${FEATURED_TURN_MAX_MS / 60_000} minutes ended your featured turn. Your score so far is final.`;
    case "operator":
      return "The stream host ended the featured turn. Thank you for playing.";
    case "disconnect":
      return "The connection was lost for too long. Your score so far is final.";
  }
}
